import type { Notification, NotificationCategory } from '@/store/slices/notificationsTypes';
import type { RetainedToastRecord } from './toastSink';

/**
 * Turn a retained toast into a bell entry.
 *
 * BADGE DISCIPLINE. Retain-by-default is only affordable because this mapping
 * separates "in the bell" from "in the badge". `isActionableUnread`
 * (notificationsSlice.ts:56) excludes `behavior === 'informational'`, so an
 * informational entry is history the officer can read and not a number
 * demanding attention. A failure is actionable and does count; everything else
 * is informational and does not. That is the mechanism that answers the
 * flooding objection — it already existed and was unreachable, along with the
 * per-category expiry, the rate limits and the dedupe windows, because
 * retention was switched off by a default resolving to "persist nothing".
 *
 * A call-page toast is categorised `call` so it inherits the existing 7-day
 * expiry; everything else is `system` (30 days). No content is shortened here:
 * the AI-caller quota message that started this work was destroyed by a
 * 150-character slice, and nothing in this path may reintroduce one.
 */

const FAILURE_VARIANTS: ReadonlySet<RetainedToastRecord['variant']> = new Set([
  'error',
  'warning',
]);

function categoryFor(record: RetainedToastRecord): NotificationCategory {
  return record.sourcePath?.startsWith('/calls/') ? 'call' : 'system';
}

export function retainedToastToNotification(
  record: RetainedToastRecord,
  userId: string | null,
): Notification {
  const isFailure = FAILURE_VARIANTS.has(record.variant);
  const behavior = isFailure ? 'actionable' : 'informational';

  return {
    // The call site's stable toast id when it gave one, so repeats of the same
    // condition collapse instead of stacking.
    id: record.id ? `toast-${record.id}` : `toast-${record.raisedAt}-${record.title.length}`,
    userId: userId || 'local',
    category: categoryFor(record),
    priority: isFailure ? 'high' : 'normal',
    behavior,
    title: record.title,
    body: record.description || '',
    actionUrl: record.sourcePath,
    metadata: { retainedToast: true, variant: record.variant },
    sourcePage: record.sourcePath,
    // Informational entries land already-read: present as history, absent from
    // the badge. A failure stays unread until the officer looks at it.
    isRead: !isFailure,
    readAt: isFailure ? undefined : record.raisedAt,
    isArchived: false,
    deliveredInapp: true,
    deliveredPush: false,
    deliveredEmail: false,
    createdAt: record.raisedAt,
  } as Notification;
}
